/**
 * Firm-level configuration and administration: aliases, rulesets, members,
 * invitations, approvals and the activity log.
 */
import { z } from 'zod';
import { roleSchema } from './enums';
import {
	analysisDataTypeSchema,
	exceptionCalculationStateSchema,
	requiredColumnSchema,
} from './analysis';

/** `GET /firm/aliases` — snake_case, unlike the Prisma-shaped `FieldAlias`. */
export const firmAliasSchema = z.object({
	pkFieldAliasId: z.string(),
	fkFirmId: z.string(),
	canonicalField: z.string(),
	spelling: z.string(),
	fkCreatedById: z.string().nullable(),
	createdAt: z.string(),
});

export const inviteRowSchema = z.object({
	pkInviteId: z.string(),
	email: z.string(),
	role: roleSchema,
	fkInvitedById: z.string().nullable(),
	expiresAt: z.string(),
	acceptedAt: z.string().nullable(),
	createdAt: z.string(),
	status: z.enum(['PENDING', 'ACCEPTED', 'EXPIRED']),
});

export const approvalItemSchema = z.object({
	type: z.enum(['REPORT', 'MAPPING']),
	id: z.string(),
	title: z.string(),
	clientName: z.string(),
	fkAuditId: z.string().optional(),
	fkFileId: z.string().optional(),
	waitingOn: z.string(),
	since: z.string().nullable(),
	confirmedByName: z.string().nullable().optional(),
});

export const activityEntrySchema = z.object({
	pkActivityLogId: z.string(),
	action: z.string(),
	entity: z.string(),
	entityId: z.string().nullable(),
	/* A jsonb column: an object, or null. Never a string — this said
     `z.string()` before the id migration too, which is why the Activity
     page has never parsed its own response. */
	detail: z.unknown(),
	actorId: z.string().nullable(),
	actorName: z.string().nullable(),
	createdAt: z.string(),
});

export const memberRoleSchema = z.object({
	role: roleSchema,
	label: z.string(),
});

/**
 * A classification rule as `GET /firm/rulesets` nests it.
 *
 * NOT the `ClassificationRule` in `misc.ts`: that mirrors the raw Prisma row
 * in camelCase, this is the wire shape, and they disagree about more than
 * case. `match_value` is a plain string for most operators, a `string[]` for
 * IN/NOT_IN and a `[low, high]` pair for BETWEEN — the same shape RuleBuilder
 * posts, never a JSON-encoded string.
 */
export const firmRuleSchema = z.object({
	pkClassificationRuleId: z.string(),
	fkRulesetId: z.string(),
	fkClientId: z.string().nullable(),
	priority: z.number(),
	matchField: z.string(),
	matchOperator: z.string(),
	matchValue: z.union([z.string(), z.array(z.string())]),
	classification: z.string(),
	reason: z.string().nullable(),
	createdAt: z.string(),
	condition: z.unknown(),
	conditionDescription: z.string().optional(),
});

/**
 * A versioned pack, as `GET /firm/rulesets` answers.
 *
 * Also not the Prisma-shaped `Ruleset`: this one is snake_case and carries its
 * rules inline, which is the whole reason the screen asks for it.
 */
export const rulesetPackSchema = z.object({
	pkRulesetId: z.string(),
	process: z.string(),
	version: z.number(),
	status: z.string(),
	note: z.string().nullable(),
	effectiveFrom: z.string().nullable(),
	createdAt: z.string(),
	rules: z.array(firmRuleSchema),
});

/**
 * One row of `GET /firm/analysis-settings` (`{ analyses: [row, ...] }`) — one
 * per analysis in the catalogue, the firm's own exception-calculation setting
 * applied. Admin only. `PATCH /firm/analysis-settings/:analysisSlug`
 * `{ exceptionCalculationEnabled }` answers with the one updated row, bare,
 * in this same shape.
 */
export const firmAnalysisSettingSchema = z.object({
	slug: z.string(),
	ref: z.string(),
	name: z.string(),
	description: z.string(),
	kind: z.enum(['SUMMARY', 'TEST']),
	dataType: analysisDataTypeSchema,
	requiredColumns: z.array(requiredColumnSchema),
	exceptionCalculation: exceptionCalculationStateSchema,
	/** Also raised as exceptions to decide on the audit's Exceptions tab. */
	raisesExceptions: z.boolean().catch(false),
});

export type FirmAlias = z.infer<typeof firmAliasSchema>;
export type FirmAnalysisSetting = z.infer<typeof firmAnalysisSettingSchema>;
export type InviteRow = z.infer<typeof inviteRowSchema>;
export type ApprovalItem = z.infer<typeof approvalItemSchema>;
export type ActivityEntry = z.infer<typeof activityEntrySchema>;
export type MemberRole = z.infer<typeof memberRoleSchema>;
export type FirmRule = z.infer<typeof firmRuleSchema>;
export type RulesetPack = z.infer<typeof rulesetPackSchema>;
